import type { FromBaseRow as Row } from './from-base-context';
import { fromBaseRecord, fromBaseUuid, FromBaseInputMissing, FromBaseStageMissing } from './from-base-context';
import type { FromBasePlayerResourceMetadata } from './from-base-player-resources';
import { createFromBaseNpcGrantContext } from './from-base-npc-stages';
import { bakeCastableLeanEffect, castableEffectCaster, castableInputFamily } from './castable-effect';
import { readAbilityEntryId } from './inventory-ability-grant-helpers';
import { readItemScalarLayerSlot } from './layer-cell-readers';
import { readLayerListEntryUuid, readInventoryField } from './inventory-raw-readers';
import { phpJsonInt, phpJsonString, comparePhpStrings } from './php-json-values';
import { plainTextDecode } from './possessor-compile';
import { phpStableSort } from './php-stable-sort';
import type { ItemFieldRow } from './item-field-reader';
import { readItemField } from './item-field-reader';

export function readNpcCastableChargeFromBase(base: Row, holder: string, id: string, maximum: number | null): unknown {
    if (!Array.isArray(base.charges)) throw new FromBaseInputMissing('castable-charge-rows', id);
    const raw = base.charges.find(raw => fromBaseRecord(raw) && phpJsonString(readItemField(raw, 'charge_holder')).toUpperCase() === holder.toUpperCase()
        && phpJsonString(readItemField(raw, 'item_id')).toUpperCase() === id.toUpperCase());
    return fromBaseRecord(raw) ? readItemField(raw, 'charges') : maximum;
}

/** Saved actors require an explicit charge SELECT; definition-only previews
 * have no saved instance and start at the definition maximum. */
export function constructNpcCastableFromBase(base: Row, metadata: FromBasePlayerResourceMetadata, host: Row,
    context: { readers: ReturnType<typeof createFromBaseNpcGrantContext>['readers']; definition: (id: string) => Row | null;
        common: Pick<ReturnType<typeof createFromBaseNpcGrantContext>['common'], 'resolveCarrierRef'> }
        = createFromBaseNpcGrantContext(base, metadata, host, 'catalog-lean-normalization'),
    buckets?: readonly string[]): void {
    const { readers, definition, common } = context;
    const field = readers.layerValue, entries = (row: ItemFieldRow | null, key: string) => Object.values(readers.layerEntries(row, key));
    const hasLayer = (row: ItemFieldRow, key: string) => field(row, key) !== null || entries(row, key).length > 0;
    const resolve = (entry: unknown): Row | null => {
        if (fromBaseRecord(entry) && fromBaseRecord(entry.item_layer_types) && Object.keys(entry.item_layer_types).length) return entry;
        const id = readItemScalarLayerSlot(entry); return fromBaseUuid(id) ? definition(id) : fromBaseRecord(entry) ? entry : null;
    };
    const choices = (row: Row, key: string): Row[] | null => {
        const list = entries(row, key).map(readLayerListEntryUuid).filter(Boolean);
        return list.length ? list.map(id => ({ id, name: plainTextDecode(phpJsonString(readers.itemField(definition(id), 'item_name') ?? '')) ?? '' })) : null;
    };
    const caster = castableEffectCaster(host, entries(host, 'passive_ability'), { layerValue: field });
    const payload: Row = {};
    const branchChoices = (row: Row): Row[] | null => {
        const ids = entries(row, 'ability_choice').map(readLayerListEntryUuid).filter(Boolean);
        return ids.length ? ids.map(id => {
            const def = definition(id), branch: Row = { id, name: plainTextDecode(phpJsonString(readers.itemField(def, 'item_name') ?? '')) ?? '' };
            if (!def || !Object.keys(def).length) return branch;
            const family = castableInputFamily(def, { layerValue: field, hasLayer, entryCount: (row, key) => entries(row, key).length });
            if (family === null) throw new FromBaseStageMissing('catalog-lean-normalization', `castable-input-family:${id}`);
            branch.input_family = family; branch.spell_level = phpJsonInt(field(def, 'spell_level'));
            const optionKey = ['user_choice_1_summon', 'user_choice_1_shapeshift'].includes(family) ? family : family === 'user_choice_1_damage_type' ? 'damage_type' : '';
            const options = optionKey ? choices(def, optionKey) : null; if (options) branch.options = options;
            return branch;
        }) : null;
    };
    function bake(def: Row, type: string | null, source: unknown, entry: Row | null): Row {
        const id = phpJsonString(readers.itemField(def, 'item_id'));
        const row: Row = { nest: { items: def.items ?? [], item_layer_types: def.item_layer_types ?? [] }, item_id: id };
        if (type !== null) row.spell_type = type;
        if (source !== null) row.source = source;
        if (type === 'innate_spell_per_long_rest') {
            const creatureId = readers.itemField(fromBaseRecord(host.encounter_creatures) ? host.encounter_creatures : null, 'encounter_creature_id');
            const live = fromBaseUuid(creatureId);
            const chargeBase = !live && base.charges === undefined ? { charges: [] } : base;
            const holder = live ? creatureId : readers.itemField(host, 'item_id');
            const charges = entry && fromBaseRecord(entry.charges) ? entry.charges : null;
            const maximum = phpJsonInt(readers.itemField(charges, 'max') ?? field(def, 'ability_charges') ?? 1);
            row.charges = { current: readNpcCastableChargeFromBase(chargeBase, phpJsonString(holder), id, maximum), max: maximum };
        } else if (type === 'charged_spell' && source !== null) {
            const inventory = entries(host, 'inventory').filter(fromBaseRecord)
                .find(row => readInventoryField(row, 'inventory_id') === source) ?? null;
            row.inventory_charges = { current: inventory ? field(inventory, 'current_item_charges') : null,
                max: inventory ? field(inventory, 'item_charges') : null,
                source_icon_type: inventory ? phpJsonString(readers.itemField(inventory, 'item_type')) : '' };
        }
        for (const [key, layer] of [['summon_choices', 'user_choice_1_summon'], ['shapeshift_choices', 'user_choice_1_shapeshift']] as const) {
            const options = choices(def, layer); if (options) row[key] = options;
        }
        const branches = branchChoices(def); if (branches) row.ability_choices = branches;
        const effect = bakeCastableLeanEffect(def, caster, { layerValue: field, hasLayer });
        if (!effect.ok) throw new FromBaseStageMissing('catalog-lean-normalization', effect.reason);
        if (effect.effect) row.effect = effect.effect;
        return row;
    }
    const known: Row[] = [], seen = new Set<string>();
    for (const key of ['spells_known', 'spell_prepared_1', 'spell_prepared_2', 'spell_prepared_3']) for (const entry of entries(host, key)) {
        const spell = resolve(entry); if (!spell) continue;
        const id = phpJsonString(readers.itemField(spell, 'item_id'));
        if (key !== 'spells_known' && seen.has(id)) continue;
        seen.add(id); known.push(spell);
    }
    const featIds = new Set(entries(host, 'feat').map(entry => readAbilityEntryId(entry, { ...readers, resolveCarrierRef: common.resolveCarrierRef })));
    const sorted = phpStableSort(known.filter(spell => entries(spell, 'requires_feat').map(readItemScalarLayerSlot).filter(Boolean).every(id => featIds.has(id))),
        (a, b) => comparePhpStrings(phpJsonString(readers.itemField(a, 'item_name')), phpJsonString(readers.itemField(b, 'item_name'))));
    let maximum = -1;
    for (const spell of sorted) if (field(spell, 'spell_level') !== null) maximum = Math.max(maximum, phpJsonInt(field(spell, 'spell_level')));
    if (sorted.length) payload.spells_known = sorted.map(spell => bake(spell, null, null, null));
    const curses = !buckets || buckets.includes('curses_known') ? entries(host, 'curses_known').map(resolve).filter(fromBaseRecord) : [];
    if (curses.length) payload.curses_known = curses.map(spell => bake(spell, null, null, null));
    for (const type of ['at_will_spell', 'innate_spell_per_long_rest', 'charged_spell']) {
        if (buckets && !buckets.includes(type)) continue;
        const list: Row[] = [];
        for (const entry of entries(host, type)) {
            if (!fromBaseRecord(entry) && !Array.isArray(entry)) continue;
            // A full definition carries items.item_id. Only a wrapper's OWN
            // item_id redirects the lookup; resolving the full row's identity
            // here would replace its nested spell with an interned cache read.
            const ref = fromBaseRecord(entry) ? readItemField({ item_arr: [entry.item_arr ?? null] }, 'item_arr')
                ?? readItemField({ item_id: [entry.item_id ?? null] }, 'item_id') ?? entry : entry;
            const def = resolve(ref); if (!def) continue;
            list.push(bake(def, type, readers.itemField(entry, 'source'), fromBaseRecord(entry) ? entry : null));
        }
        if (list.length) payload[type] = list;
    }
    const computed = host.computed as Row, creature = computed.creature as Row;
    (creature.spellcasting as Row).max_spell_level_castable = maximum;
    creature.castable = Object.keys(payload).length ? payload : [];
    computed._cache = { ...(fromBaseRecord(computed._cache) ? computed._cache : {}), castable_v: 2 };
}
